import { CSSProperties } from 'react';
import { useGridIsLoading } from '../../../../store/selectors/useGridIsLoading';
import { useMinimumLoadingDuration } from './hooks/useMinimumLoadingDuration';

// Inline styles — cannot be Tailwind classes because the animation name
// (eag-grid-loading-slide) is defined as a custom keyframe in tailwind.css.
const LOADING_BAR_STYLE: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 2,
  overflow: 'hidden',
  zIndex: 'var(--z-content-loader)',
  pointerEvents: 'none'
};
const LOADING_BAR_INNER_STYLE: CSSProperties = {
  width: '40%',
  height: '100%',
  background: 'linear-gradient(90deg, transparent, var(--color-blue-400), transparent)',
  animation: 'eag-grid-loading-slide 1.5s ease-in-out infinite'
};

/**
 * Thin animated progress bar that appears at the bottom edge of GridHeader
 * while data is fetching. Reads loading state directly from the store — no props,
 * no re-renders in parent components.
 *
 * Uses `position: absolute; bottom: 0` so it sits flush with the bottom border
 * of GridHeader without affecting layout. The parent (GridHeader root div)
 * must have `position: relative`.
 *
 * `useMinimumLoadingDuration` guarantees the bar stays visible for at least one
 * full animation cycle (1.5 s) even when the network response is very fast.
 */
export function LoadingBar() {
  const isLoading = useGridIsLoading();
  const showLoadingBar = useMinimumLoadingDuration(isLoading);
  if (!showLoadingBar) {
    return null;
  }
  return <div role="status" aria-label="Loading data" style={LOADING_BAR_STYLE}>
      <div style={LOADING_BAR_INNER_STYLE} />
    </div>;
}